import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { Field, Input, Textarea, Btn, Select } from "@/components/admin/AdminUI";
import ImageUpload from "@/components/admin/ImageUpload";

const TIMEZONES = [
  "America/Santo_Domingo", "America/New_York", "America/Chicago", "America/Denver",
  "America/Los_Angeles", "America/Mexico_City", "America/Bogota", "America/Lima",
  "America/Santiago", "America/Argentina/Buenos_Aires", "America/Sao_Paulo",
  "Europe/Madrid", "Europe/London", "UTC",
];

const EMPTY = {
  site_name: "", description: "", email: "", phone: "", footer_text: "", ga_id: "", logo: "", favicon: "", timezone: "America/Santo_Domingo",
  categories_bg_video: "", categories_bg_poster: "", categories_bg_audio: "",
  socials: { instagram: "", facebook: "", twitter: "", youtube: "" },
};

export default function SettingsAdmin() {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { refresh } = useSite();

  useEffect(() => { api.get("/settings").then((r) => setForm({ ...EMPTY, ...r.data, socials: { ...EMPTY.socials, ...(r.data?.socials || {}) } })); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSocial = (k, v) => setForm((f) => ({ ...f, socials: { ...f.socials, [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form }; delete payload.key; delete payload.menu;
      await api.put("/admin/settings", payload);
      toast.success("Configuración guardada"); refresh();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <header className="mb-8">
        <h1 className="font-serif text-4xl tracking-tight">Configuración del sitio</h1>
      </header>

      <div className="bg-white border border-sunset-border p-8">
        <Field label="Nombre de la publicación"><Input data-testid="settings-site-name" value={form.site_name} onChange={(e) => set("site_name", e.target.value)} /></Field>
        <Field label="Descripción"><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-6 mb-4">
          <ImageUpload label="Logo" value={form.logo} onChange={(v) => set("logo", v)} />
          <ImageUpload label="Favicon" value={form.favicon} onChange={(v) => set("favicon", v)} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Email"><Input value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
          <Field label="Teléfono"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
        </div>
        <p className="text-xs font-semibold uppercase tracking-wider text-sunset-muted mt-4 mb-3">Redes sociales</p>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Instagram"><Input value={form.socials.instagram} onChange={(e) => setSocial("instagram", e.target.value)} /></Field>
          <Field label="Facebook"><Input value={form.socials.facebook} onChange={(e) => setSocial("facebook", e.target.value)} /></Field>
          <Field label="Twitter / X"><Input value={form.socials.twitter} onChange={(e) => setSocial("twitter", e.target.value)} /></Field>
          <Field label="YouTube"><Input value={form.socials.youtube} onChange={(e) => setSocial("youtube", e.target.value)} /></Field>
        </div>
        <Field label="Texto del footer"><Input value={form.footer_text} onChange={(e) => set("footer_text", e.target.value)} /></Field>
        <Field label="Google Analytics ID" hint="Ej: G-XXXXXXX"><Input value={form.ga_id} onChange={(e) => set("ga_id", e.target.value)} /></Field>
        <Field label="Zona horaria de publicación" hint="Se usa para programar y publicar artículos con precisión">
          <Select value={form.timezone} onChange={(e) => set("timezone", e.target.value)} data-testid="settings-timezone">
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </Select>
        </Field>
        <div className="mt-4"><Btn variant="accent" onClick={save} disabled={saving} data-testid="save-settings-btn">{saving ? "Guardando…" : "Guardar cambios"}</Btn></div>
      </div>

      <div className="bg-white border border-sunset-border p-8 mt-6">
        <h2 className="font-serif text-2xl tracking-tight mb-1">Página de Categorías</h2>
        <p className="text-sunset-muted text-sm mb-6">Personaliza el fondo animado y el sonido ambiente de la sección Categorías.</p>
        <div className="mb-6"><ImageUpload label="Video de fondo (MP4)" value={form.categories_bg_video} onChange={(v) => set("categories_bg_video", v)} accept="video/mp4,video/webm" /></div>
        <div className="mb-6"><ImageUpload label="Imagen de respaldo (poster)" value={form.categories_bg_poster} onChange={(v) => set("categories_bg_poster", v)} /></div>
        <div className="mb-6"><ImageUpload label="Sonido de olas (audio)" value={form.categories_bg_audio} onChange={(v) => set("categories_bg_audio", v)} accept="audio/*" /></div>
        <Btn variant="accent" onClick={save} disabled={saving} data-testid="save-categories-bg-btn">{saving ? "Guardando…" : "Guardar cambios"}</Btn>
      </div>
    </div>
  );
}
